import type { ReactNode } from 'react';
import { apiClient } from '@/api/client';
import { useAuth as useShellAuth } from '@/contexts/AuthContext';
import { usePermissions as useShellPermissions } from '@/hooks/usePermissions';
import { desktopTicketPrinter, isDesktopApp } from '@/lib/desktop';
import {
  articleKeys,
  fetchArticleByCode,
  fetchArticles,
  useArticle,
  useArticleCategories,
  useArticles,
} from '@/api/queries/articles';
import { useClients } from '@/api/queries/clients';
import { useEstablishments } from '@/api/queries/establishments';
import { useJurisdiction, useVatRates } from '@/api/queries/jurisdiction';
import { useSessionDefaultLocation, useStockLocations } from '@/api/queries/stock';
import { useBankReferencesStatus } from '@/api/queries/settings';
import { usePosPaymentMethods } from '@/api/queries/ui-preferences';
import { useFromFileQuote } from '@/api/queries/draft-proposal-quote';
import { ShellInvoicePaymentRequest, ShellSourceFileNote } from './billing-slots';
import { WebBillingHostProvider } from '@efacture/web-billing/billing-host/context';
import type { WebBillingHost } from '@efacture/web-billing/billing-host/types';

/**
 * The shell's implementations of what billing reads from its host. Holds no
 * state; each member calls the shell module when billing calls it.
 */
export const SHELL_BILLING_HOST: WebBillingHost = {
  slots: {
    InvoicePaymentRequest: ShellInvoicePaymentRequest,
    SourceFileNote: ShellSourceFileNote,
    useFromFileQuote: () => useFromFileQuote(),
  },
  api: apiClient,
  session: { useAuth: () => useShellAuth() },
  permissions: { usePermissions: () => useShellPermissions() },
  desktop: {
    isDesktopApp: () => isDesktopApp(),
    desktopTicketPrinter: () => desktopTicketPrinter(),
  },
  articles: {
    articleKeys: {
      list: (params) => articleKeys.list(params),
      byCode: (code) => articleKeys.byCode(code),
    },
    useArticles: (params, options) => useArticles(params, options),
    useArticle: (id, options) => useArticle(id, options),
    useArticleCategories: (options) => useArticleCategories(options),
    fetchArticles: (params) => fetchArticles(params),
    fetchArticleByCode: (code) => fetchArticleByCode(code),
  },
  clients: { useClients: (params, options) => useClients(params, options) },
  establishments: { useEstablishments: (includeInactive, options) => useEstablishments(includeInactive, options) },
  jurisdiction: {
    useJurisdiction: (options) => useJurisdiction(options),
    useVatRates: (options) => useVatRates(options),
  },
  stock: {
    useStockLocations: (options) => useStockLocations(options),
    useSessionDefaultLocation: (options) => useSessionDefaultLocation(options),
  },
  settings: { useBankReferencesStatus: () => useBankReferencesStatus() },
  payments: { usePaymentMethods: () => usePosPaymentMethods() },
};

/** Mounts the shell's billing host for every route. */
export function BillingHostProvider({ children }: { children: ReactNode }) {
  return <WebBillingHostProvider host={SHELL_BILLING_HOST}>{children}</WebBillingHostProvider>;
}
